import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/pfc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Check, Music2, Disc3, Radio, PlayCircle, Upload, X } from "lucide-react";

const MV_KEYS = ["mv_single", "mv_triple", "mv_unlimited"];

const MV_ICONS = {
  mv_single: Music2,
  mv_triple: Disc3,
  mv_unlimited: Radio,
};

const MV_FEATURES = {
  mv_single: [
    "1 music video featured on the Music Videos page",
    "Lives on page for 30 days",
    "$19/month after to keep it live",
    "Embedded YouTube player + artist credit",
  ],
  mv_triple: [
    "3 music videos posted per month",
    "All 3 live for the full 30-day cycle",
    "Best value for active artists",
    "Bundle saves vs. single drops",
  ],
  mv_unlimited: [
    "Unlimited music video posts",
    "Each video lives 30 days",
    "$19/mo per video to keep beyond 30 days",
    "Ideal for labels & repeat releases",
  ],
};

const PILL_HIGHLIGHT = "mv_triple";

function extractYouTubeId(input) {
  if (!input) return null;
  const trimmed = input.trim();
  // already a raw ID (11 chars typical)
  if (/^[A-Za-z0-9_-]{6,15}$/.test(trimmed) && !trimmed.includes("/")) return trimmed;
  try {
    const url = new URL(trimmed);
    if (url.hostname.includes("youtu.be")) {
      const id = url.pathname.split("/").filter(Boolean)[0];
      return id || null;
    }
    if (url.hostname.includes("youtube.com")) {
      if (url.searchParams.get("v")) return url.searchParams.get("v");
      // /embed/<id> or /shorts/<id>
      const parts = url.pathname.split("/").filter(Boolean);
      const idx = parts.findIndex((p) => p === "embed" || p === "shorts");
      if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
    }
  } catch {
    return null;
  }
  return null;
}

export default function MusicVideos() {
  const { user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [pkgs, setPkgs] = useState([]);
  const [loadingKey, setLoadingKey] = useState(null);
  const [videosLoading, setVideosLoading] = useState(true);
  const [quota, setQuota] = useState(null);
  const [showSubmit, setShowSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ url: "", title: "", artist: "" });

  const loadVideos = useCallback(() => {
    setVideosLoading(true);
    api.get("/music-videos")
      .then((r) => setVideos(r.data))
      .catch(() => {})
      .finally(() => setVideosLoading(false));
  }, []);

  const loadQuota = useCallback(() => {
    if (!user) { setQuota(null); return; }
    api.get("/music-videos/me/quota")
      .then((r) => setQuota(r.data))
      .catch(() => setQuota(null));
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    loadVideos();
    api.get("/packages")
      .then((r) => {
        if (cancelled) return;
        const mv = r.data.filter((p) => MV_KEYS.includes(p.key));
        mv.sort((a, b) => MV_KEYS.indexOf(a.key) - MV_KEYS.indexOf(b.key));
        setPkgs(mv);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [loadVideos]);

  useEffect(() => { loadQuota(); }, [loadQuota]);

  const checkout = async (key) => {
    setLoadingKey(key);
    try {
      const { data } = await api.post("/checkout/session", {
        package_id: key,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setLoadingKey(null);
    }
  };

  const submitVideo = async (e) => {
    e.preventDefault();
    const youtube_id = extractYouTubeId(form.url);
    if (!youtube_id) {
      toast.error("Couldn't read that YouTube link — paste the full URL (e.g. https://youtu.be/abc123).");
      return;
    }
    if (!form.title.trim() || !form.artist.trim()) {
      toast.error("Title and artist are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/music-videos", {
        youtube_id,
        title: form.title.trim(),
        artist: form.artist.trim(),
      });
      toast.success("Video submitted — now live on the Music Videos page.");
      setForm({ url: "", title: "", artist: "" });
      setShowSubmit(false);
      loadVideos();
      loadQuota();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = quota && quota.has_paid_package && quota.remaining > 0;

  return (
    <div data-testid="music-videos-page">
      {/* HERO */}
      <section className="relative py-20 border-b border-white/5 splatter-blue grain">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-10 relative">
          <div className="label text-pfc-gold mb-3">PFC · Music Videos</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white uppercase leading-[0.9]">
            Featured <span className="gold-text">Music Videos</span><br />
            <span className="silver-text">Loud · Loud · Loud.</span>
          </h1>
          <p className="text-white/65 mt-6 font-body text-lg max-w-2xl">
            A curated wall of music videos from the PFC community. Get your video on this page — paid, featured, in front of our magazine readers and newsletter subscribers.
          </p>
        </div>
      </section>

      {/* QUOTA / SUBMIT BANNER (logged-in paid users) */}
      {canSubmit && (
        <section className="bg-pfc-gold/10 border-b border-pfc-gold/30" data-testid="mv-quota-banner">
          <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-6 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="label text-pfc-gold mb-1">Your Music Video Quota</div>
              <p className="font-display text-white text-xl">
                {quota.videos_used} <span className="text-white/40">/</span> {quota.videos_total === 9999 ? "Unlimited" : quota.videos_total} used · {quota.remaining === 9999 ? "Unlimited" : quota.remaining} remaining
              </p>
            </div>
            <button
              onClick={() => setShowSubmit(true)}
              className="font-sub uppercase tracking-[0.25em] text-sm px-6 py-3 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition flex items-center gap-2"
              data-testid="mv-open-submit-btn"
            >
              <Upload size={16} /> Submit Your Video
            </button>
          </div>
        </section>
      )}

      {/* FEATURED VIDEO GRID */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16" data-testid="music-videos-grid-section">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <div className="label text-pfc-gold mb-2">Currently Featured</div>
            <h2 className="font-display text-3xl sm:text-4xl text-white uppercase leading-tight">
              Now <span className="gold-text">Playing</span> on PFC
            </h2>
          </div>
          {!canSubmit && (
            <a
              href="#pricing"
              className="font-sub uppercase tracking-[0.2em] text-xs px-5 py-3 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition"
              data-testid="mv-get-featured-cta"
            >
              Get Your Video Featured →
            </a>
          )}
        </div>

        {videosLoading ? (
          <div className="text-white/40 text-center py-16 font-sub uppercase tracking-[0.2em]" data-testid="mv-loading">
            Loading featured videos…
          </div>
        ) : videos.length === 0 ? (
          <div className="text-white/40 text-center py-16 font-sub uppercase tracking-[0.2em]" data-testid="mv-empty">
            No videos featured yet · be the first.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="mv-grid">
            {videos.map((v) => (
              <article
                key={v.video_id}
                className="pfc-card p-5 flex flex-col"
                data-testid={`mv-card-${v.video_id}`}
              >
                <div className="relative w-full overflow-hidden mb-4" style={{ paddingTop: "56.25%", borderRadius: 10 }}>
                  <iframe
                    title={v.title}
                    src={`https://www.youtube.com/embed/${v.youtube_id}?rel=0&modestbranding=1`}
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-display text-xl text-white uppercase truncate">{v.title}</h3>
                    <p className="font-sub text-xs uppercase tracking-[0.2em] text-pfc-gold mt-1">
                      {v.artist}
                    </p>
                  </div>
                  <PlayCircle className="text-pfc-gold shrink-0" size={28} />
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* PRICING TIERS */}
      <section
        id="pricing"
        className="border-t border-white/5 py-20 bg-ink-900"
        data-testid="mv-pricing-section"
      >
        <div className="max-w-[1200px] mx-auto px-5 lg:px-10">
          <div className="text-center mb-12">
            <div className="label text-pfc-gold mb-3">Music Video Packages</div>
            <h2 className="font-display text-3xl sm:text-5xl text-white uppercase leading-[0.95]">
              Get Your Video on <span className="gold-text">This Page.</span>
            </h2>
            <p className="text-white/60 mt-5 font-body max-w-xl mx-auto">
              Three ways to feature your music video on PFC. Secure Stripe checkout · pay once, post fast.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="mv-pricing-grid">
            {pkgs.map((p) => {
              const Icon = MV_ICONS[p.key] || Music2;
              const highlight = p.key === PILL_HIGHLIGHT;
              return (
                <div
                  key={p.key}
                  className={`pfc-card relative p-8 lg:p-10 flex flex-col ${highlight ? "lg:scale-105 border-pfc-gold/50" : ""}`}
                  data-testid={`mv-pricing-card-${p.key}`}
                >
                  {highlight && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 label bg-pfc-gold text-ink-900 px-3 py-1.5">
                      Best Value
                    </span>
                  )}
                  <Icon className="text-pfc-gold mb-5" size={32} />
                  <h3 className="font-display text-2xl text-white uppercase leading-tight">{p.name}</h3>
                  <p className="text-white/60 mt-3 font-body text-sm">{p.description}</p>

                  <div className="mt-8 mb-2">
                    <span className="font-display text-6xl gold-text">${Math.floor(p.amount)}</span>
                    <span className="font-sub uppercase tracking-[0.2em] text-xs text-white/50 ml-2">
                      USD · one-time
                    </span>
                  </div>
                  {p.retention_fee > 0 ? (
                    <p className="text-white/50 font-body text-xs mb-6" data-testid={`mv-retention-${p.key}`}>
                      + ${Math.floor(p.retention_fee)}/month to keep video live after 30 days
                    </p>
                  ) : (
                    <div className="mb-6" />
                  )}

                  <ul className="space-y-3 mb-10 flex-1">
                    {(MV_FEATURES[p.key] || []).map((f) => (
                      <li key={f} className="flex items-start gap-3 text-white/80 font-body text-sm">
                        <Check className="text-pfc-gold shrink-0 mt-0.5" size={16} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => checkout(p.key)}
                    disabled={loadingKey === p.key}
                    className={`w-full font-sub uppercase tracking-[0.25em] text-sm py-4 transition disabled:opacity-50 ${
                      highlight
                        ? "bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright"
                        : "border border-white/20 text-white hover:border-pfc-gold hover:text-pfc-gold"
                    }`}
                    data-testid={`mv-checkout-${p.key}`}
                  >
                    {loadingKey === p.key ? "Redirecting…" : `Buy ${p.name.split("—")[0].trim()}`}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-16 text-center" data-testid="mv-pricing-footnote">
            {user ? (
              <p className="text-white/55 font-body max-w-2xl mx-auto">
                After payment, return to this page — the "Submit Your Video" button will appear above the grid automatically.
              </p>
            ) : (
              <p className="text-white/55 font-body max-w-2xl mx-auto">
                After payment, <Link to="/login" className="text-pfc-gold hover:underline">sign in</Link> and submit your YouTube link right from this page.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* SUBMIT MODAL */}
      {showSubmit && (
        <div
          className="fixed inset-0 bg-ink-900/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={() => !submitting && setShowSubmit(false)}
          data-testid="mv-submit-modal"
        >
          <div
            className="bg-ink-800 border border-pfc-gold/30 w-full max-w-xl p-8 lg:p-10 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => !submitting && setShowSubmit(false)}
              className="absolute top-4 right-4 text-white/60 hover:text-white p-2"
              data-testid="mv-submit-close"
            >
              <X size={20} />
            </button>
            <div className="label text-pfc-gold mb-2">Submit Music Video</div>
            <h3 className="font-display text-3xl text-white uppercase leading-tight mb-2">
              Drop Your <span className="gold-text">Track.</span>
            </h3>
            <p className="text-white/60 font-body text-sm mb-6">
              Paste your YouTube URL and tell us what we're listening to. Goes live immediately for 30 days.
            </p>

            <form onSubmit={submitVideo} className="space-y-4" data-testid="mv-submit-form">
              <div>
                <label className="label text-white/70 mb-2 block">YouTube URL</label>
                <input
                  type="url"
                  required
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="https://youtu.be/abc123"
                  className="w-full bg-ink-900 border border-white/15 text-white px-4 py-3 font-body focus:border-pfc-gold focus:outline-none"
                  data-testid="mv-submit-url"
                />
              </div>
              <div>
                <label className="label text-white/70 mb-2 block">Title</label>
                <input
                  type="text"
                  required
                  maxLength={120}
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Track name"
                  className="w-full bg-ink-900 border border-white/15 text-white px-4 py-3 font-body focus:border-pfc-gold focus:outline-none"
                  data-testid="mv-submit-title"
                />
              </div>
              <div>
                <label className="label text-white/70 mb-2 block">Artist</label>
                <input
                  type="text"
                  required
                  maxLength={120}
                  value={form.artist}
                  onChange={(e) => setForm({ ...form, artist: e.target.value })}
                  placeholder="Artist name / @handle"
                  className="w-full bg-ink-900 border border-white/15 text-white px-4 py-3 font-body focus:border-pfc-gold focus:outline-none"
                  data-testid="mv-submit-artist"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full font-sub uppercase tracking-[0.25em] text-sm py-4 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="mv-submit-confirm"
              >
                <Upload size={16} /> {submitting ? "Posting…" : "Publish to PFC"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
